import React, { useRef, useEffect, useState, useCallback } from "react";
import styles from "./TerminalPositioner2D.module.css";

interface TerminalPositioner2DProps {
  floorMapUrl: string;
  initialPositionX?: number;
  initialPositionY?: number;
  initialPositionZ?: number;
  onPositionChange: (position: { x: number; y: number; z: number }) => void;
}

export default function TerminalPositioner2D({
  floorMapUrl,
  initialPositionX = 0,
  initialPositionY = 0,
  initialPositionZ = 0,
  onPositionChange,
}: TerminalPositioner2DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  const [position, setPosition] = useState({
    x: Number(initialPositionX) || 0,
    y: Number(initialPositionY) || 0,
    z: Number(initialPositionZ) || 0,
  });
  const [isPanning, setIsPanning] = useState(false);
  const [isDraggingTerminal, setIsDraggingTerminal] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [floorMap, setFloorMap] = useState<HTMLImageElement | null>(null);
  const [terminalIcon, setTerminalIcon] = useState<HTMLImageElement | null>(
    null
  );

  // Update pan ref when pan changes
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  // Update position when initial positions change
  useEffect(() => {
    setPosition({
      x: Number(initialPositionX) || 0,
      y: Number(initialPositionY) || 0,
      z: Number(initialPositionZ) || 0,
    });
  }, [initialPositionX, initialPositionY, initialPositionZ]);

  // Load floor map
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setFloorMap(img);
    img.onerror = () => console.error("Failed to load floor map");
    img.src = floorMapUrl;
  }, [floorMapUrl]);

  // Load terminal icon
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const mediaBase = process.env.REACT_APP_MEDIA_PATH;
    img.onload = () => setTerminalIcon(img);
    img.onerror = () => console.error("Failed to load terminal icon");
    img.src = `${mediaBase}/uploads/terminal/terminal.svg`;
  }, []);

  // Draw function
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate scale to fit if map exists
    let scaleToFit = 1;
    if (floorMap) {
      scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9 // Leave 10% margin on each side
      );
    }

    // Draw floor map with zoom and pan (matching MallMap approach)
    if (floorMap) {
      // Save context
      ctx.save();

      // Calculate scaled dimensions with scale applied
      const scaledWidth = floorMap.width * scaleToFit * scale;
      const scaledHeight = floorMap.height * scaleToFit * scale;

      // Center position with pan offset
      const mapX = (canvas.width - scaledWidth) / 2 + pan.x;
      const mapY = (canvas.height - scaledHeight) / 2 + pan.y;

      // Draw map with scaling and pan
      ctx.drawImage(floorMap, mapX, mapY, scaledWidth, scaledHeight);

      // Restore context
      ctx.restore();
    }

    // Draw terminal icon with same coordinate system as map
    ctx.save();

    // Apply transformations to match map position and scale
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.translate(pan.x, pan.y);
    ctx.scale(scale * scaleToFit, scale * scaleToFit);

    // Calculate terminal position
    const posX = Number(position.x) || 0;
    const posY = Number(position.y) || 0;

    // Terminal position in scaled coordinates
    const terminalCanvasX = posX * 100;
    const terminalCanvasY = posY * 100;

    // Draw terminal icon if available
    if (terminalIcon) {
      // Apply scaleX and scaleY (default 1.0) to match MallMap rendering
      // Note: scaleX and scaleY are applied to the icon size before context scaling
      const scaleX = 1.0;
      const scaleY = 1.0;
      // Calculate icon dimensions with scaleX/scaleY applied
      // The context scaling (scale * mapScale) will be applied automatically
      const iconWidth = terminalIcon.width * scaleX;
      const iconHeight = terminalIcon.height * scaleY;
      ctx.drawImage(
        terminalIcon,
        terminalCanvasX - iconWidth / 2,
        terminalCanvasY - iconHeight / 2,
        iconWidth,
        iconHeight
      );
    } else {
      // Draw a placeholder marker if icon is not loaded
      ctx.fillStyle = "rgba(234, 126, 102, 0.8)";
      ctx.strokeStyle = "white";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(terminalCanvasX, terminalCanvasY, 20, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Draw inner circle
      ctx.fillStyle = "white";
      ctx.beginPath();
      ctx.arc(terminalCanvasX, terminalCanvasY, 8, 0, Math.PI * 2);
      ctx.fill();
    }

    // Restore context
    ctx.restore();
  }, [floorMap, scale, pan, position, terminalIcon]);

  // Update canvas size and redraw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      draw();
    };

    resize();
    
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [draw]);

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate map scale to fit
    const scaleToFit = Math.min(
      canvas.width / floorMap.width,
      canvas.height / floorMap.height,
      0.9
    );
    const totalScale = scale * scaleToFit;

    // Calculate terminal position in canvas coordinates
    // After transform: translate(canvas.width/2, canvas.height/2) + translate(pan.x, pan.y) + scale(...)
    // So terminal at (posX * 100, posY * 100) in transformed space
    // Maps to: canvas.width/2 + pan.x + posX * 100 * totalScale
    const posX = Number(position.x) || 0;
    const posY = Number(position.y) || 0;
    const terminalCanvasX = canvas.width / 2 + pan.x + posX * 100 * totalScale;
    const terminalCanvasY = canvas.height / 2 + pan.y + posY * 100 * totalScale;

    // Calculate distance from mouse to terminal icon center
    // Icon size needs to account for scaleX/scaleY and totalScale
    let iconRadius = 30 * totalScale; // Default marker radius scaled
    if (terminalIcon) {
      const iconSize =
        Math.max(terminalIcon.width, terminalIcon.height) * totalScale;
      iconRadius = iconSize / 2;
    }
    const dx = mouseX - terminalCanvasX;
    const dy = mouseY - terminalCanvasY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If clicking on terminal icon, start dragging it
    if (distance < iconRadius + 20) {
      setIsDraggingTerminal(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
      e.preventDefault();
      return;
    }

    // Otherwise, start panning
    setIsPanning(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    if (isDraggingTerminal) {
      // Calculate new position for terminal marker
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate map scale to fit
      const scaleToFit = Math.min(
        canvas.width / floorMap.width,
        canvas.height / floorMap.height,
        0.9
      );
      const totalScale = scale * scaleToFit;

      // Convert mouse position to terminal coordinates
      const x = (mouseX - canvas.width / 2 - pan.x) / totalScale;
      const y = (mouseY - canvas.height / 2 - pan.y) / totalScale;

      const newX = x / 100;
      const newY = y / 100;
      const newZ = Number(initialPositionZ) || 0;

      setPosition({
        x: newX,
        y: newY,
        z: newZ,
      });

      onPositionChange({
        x: newX,
        y: newY,
        z: newZ,
      });

      setLastMousePos({ x: e.clientX, y: e.clientY });
    } else if (isPanning) {
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;
      setPan((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsPanning(false);
    setIsDraggingTerminal(false);
  };

  // Handle double click to place terminal
  const handleDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const rect = canvas.getBoundingClientRect();
    // Get mouse position relative to canvas
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate map scale to fit
    const scaleToFit = Math.min(
      canvas.width / floorMap.width,
      canvas.height / floorMap.height,
      0.9
    );

    // Calculate position relative to center of canvas (accounting for pan and scale)
    // Center of canvas is the origin for terminal coordinates
    const totalScale = scale * scaleToFit;
    const x = (mouseX - canvas.width / 2 - pan.x) / totalScale;
    const y = (mouseY - canvas.height / 2 - pan.y) / totalScale;

    // Convert to terminal coordinates (divide by 100 as per original logic)
    const newX = x / 100;
    const newY = y / 100;
    const newZ = Number(initialPositionZ) || 0;

    setPosition({
      x: newX,
      y: newY,
      z: newZ,
    });

    onPositionChange({
      x: newX,
      y: newY,
      z: newZ,
    });
  };

  // Handle wheel for zooming with addEventListener to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !floorMap) return;

    const handleWheelCapture = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setScale((prev) => {
        const newScale = Math.max(0.5, Math.min(5, prev * delta));
        const scaleToFit = Math.min(
          canvas.width / floorMap.width,
          canvas.height / floorMap.height,
          0.9
        );
        const currentPan = panRef.current;
        const imageX = (mouseX - canvas.width / 2 - currentPan.x) / (scaleToFit * prev);
        const imageY = (mouseY - canvas.height / 2 - currentPan.y) / (scaleToFit * prev);
        setPan((prevPan) => ({
          x: prevPan.x - imageX * scaleToFit * (newScale - prev),
          y: prevPan.y - imageY * scaleToFit * (newScale - prev),
        }));
        return newScale;
      });
    };

    canvas.addEventListener("wheel", handleWheelCapture, {
      passive: false,
      capture: true,
    });

    return () => {
      canvas.removeEventListener("wheel", handleWheelCapture, {
        capture: true,
      });
    };
  }, [floorMap]);

  return (
    <div className={styles.container}>
      {/* <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <label className={styles.label}>Масштаб</label>
          <input
            type="range"
            min="0.5"
            max="3"
            step="0.1"
            value={scale}
            onChange={handleScaleChange}
            className={styles.slider}
          />
          <div className={styles.value}>{scale.toFixed(1)}x</div>
        </div>
        <button className={styles.button} onClick={handleReset}>
          Сбросить
        </button>
      </div> */}
      <canvas
        className={styles.canvas}
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      />

      <div className={styles.instructions}>
        Двойной клик - установить позицию | Перетаскивание маркера - переместить
        | Скролл - масштабирование | Перетаскивание карты - панорамирование
      </div>
    </div>
  );
}
